import { useState } from "react";
import { useRegenerateCanonicalBrief } from "../../hooks/useBriefDraft";

/** States in which nothing has been drafted yet, so the brief can be rebuilt (mirrors the server). */
const REGENERABLE_STATES = new Set(["ideation", "researching", "briefed", "blocked"]);

/**
 * Walkthrough item 3: rebuild the brief from its Opportunities idea. Replaces the brief text
 * (earlier revisions stay in history), so the operator confirms first.
 */
export function RegenerateCanonicalBrief({ brandId, campaignId, brief }: {
  brandId: string;
  campaignId: string;
  brief: { id: string; state: string };
}) {
  const regenerate = useRegenerateCanonicalBrief(brandId, campaignId);
  const [confirming, setConfirming] = useState(false);
  if (!REGENERABLE_STATES.has(brief.state)) return null;
  return (
    <section aria-label="Regenerate brief" className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-5 py-3 text-sm">
      {confirming ? <>
        <span className="text-slate-700">Rebuild this brief from its idea? Your edits stay in the revision history.</span>
        <button type="button" disabled={regenerate.isPending}
          onClick={() => regenerate.mutate(brief.id, { onSettled: () => setConfirming(false) })}
          className="min-h-11 rounded-lg bg-brand-600 px-3 font-medium text-white hover:bg-brand-700 disabled:opacity-50">
          {regenerate.isPending ? "Regenerating…" : "Regenerate"}
        </button>
        <button type="button" onClick={() => setConfirming(false)} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 font-medium text-slate-800">Cancel</button>
      </> : <button type="button" onClick={() => { regenerate.reset(); setConfirming(true); }}
        className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 font-medium text-slate-800 hover:bg-slate-50">
        Regenerate canonical brief
      </button>}
      {regenerate.isSuccess && <p role="status" className="w-full text-emerald-800">Brief regenerated from its idea.</p>}
      {regenerate.isError && <p role="alert" className="w-full text-amber-900">{regenerate.error instanceof Error ? regenerate.error.message : "The brief could not be regenerated."}</p>}
    </section>
  );
}
